import { useState, useEffect, useRef } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { Trophy } from 'lucide-react'
import { useAuth } from '../context/AuthContext'
import { supabase } from '../lib/supabase'

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'https://hireit-production-ac0f.up.railway.app'

export default function Header() {
const { user, profile, signOut } = useAuth()
const navigate = useNavigate()
const [menuOpen, setMenuOpen] = useState(false)
const [unreadCount, setUnreadCount] = useState(0)
const [pendingBookings, setPendingBookings] = useState(0)
const [acceptedUnpaid, setAcceptedUnpaid] = useState(0)
const [newReviews, setNewReviews] = useState(0)
const [newReferrals, setNewReferrals] = useState(0)
const [showFoundingModal, setShowFoundingModal] = useState(false)
const [foundingSuburb, setFoundingSuburb] = useState('')
const unreadSubRef = useRef(null)
const bookingSubRef = useRef(null)
const reviewSubRef = useRef(null)
const menuRef = useRef(null)
const intervalRef = useRef(null)
const foundingCheckedRef = useRef(false)

useEffect(() => {
if (!user) { setUnreadCount(0); setPendingBookings(0); setAcceptedUnpaid(0); setNewReviews(0); setNewReferrals(0); return }
fetchUnreadCount()
fetchPendingBookings()
fetchAcceptedUnpaid()
fetchNewReviews()
fetchNewReferrals()
subscribeToUnread()
subscribeToBookings()
subscribeToReviews()
intervalRef.current = setInterval(() => {
fetchUnreadCount()
fetchPendingBookings()
fetchAcceptedUnpaid()
fetchNewReviews()
fetchNewReferrals()
}, 5000)
return () => {
if (unreadSubRef.current) unreadSubRef.current.unsubscribe()
if (bookingSubRef.current) bookingSubRef.current.unsubscribe()
if (reviewSubRef.current) reviewSubRef.current.unsubscribe()
if (intervalRef.current) clearInterval(intervalRef.current)
}
}, [user])

useEffect(() => {
if (!user || !profile) return
if (foundingCheckedRef.current) return
if (profile.founding_lister_prompt_seen_at) return
const suburb = profile.suburb?.trim()
if (!suburb) return
foundingCheckedRef.current = true

const checkFoundingLister = async () => {
const trimmedLower = suburb.toLowerCase()
const { data: rows } = await supabase
.from('listings')
.select('suburb')
.ilike('suburb', `%${suburb}%`)
const hasMatch = (rows || []).some(r => r.suburb?.trim().toLowerCase() === trimmedLower)
if (hasMatch) return

setFoundingSuburb(suburb)
setShowFoundingModal(true)
supabase
.from('profiles')
.update({ founding_lister_prompt_seen_at: new Date().toISOString() })
.eq('id', user.id)
.then(() => {})
}
checkFoundingLister()
}, [user, profile])

useEffect(() => {
const handleClickOutside = (e) => {
if (menuRef.current && !menuRef.current.contains(e.target)) {
setMenuOpen(false)
}
}
if (menuOpen) {
document.addEventListener('mousedown', handleClickOutside)
document.addEventListener('touchstart', handleClickOutside)
}
return () => {
document.removeEventListener('mousedown', handleClickOutside)
document.removeEventListener('touchstart', handleClickOutside)
}
}, [menuOpen])

const fetchUnreadCount = async () => {
const { data: bookings } = await supabase
.from('bookings')
.select('id')
.or(`hirer_id.eq.${user.id},owner_id.eq.${user.id}`)
if (!bookings?.length) { setUnreadCount(0); return }
const bookingIds = bookings.map(b => b.id)
const { count } = await supabase
.from('messages')
.select('*', { count: 'exact', head: true })
.in('booking_id', bookingIds)
.eq('read', false)
.neq('sender_id', user.id)
setUnreadCount(count || 0)
}

const fetchPendingBookings = async () => {
const { count } = await supabase
.from('bookings')
.select('*', { count: 'exact', head: true })
.eq('owner_id', user.id)
.eq('status', 'pending')
setPendingBookings(count || 0)
}

const fetchAcceptedUnpaid = async () => {
const { count } = await supabase
.from('bookings')
.select('*', { count: 'exact', head: true })
.eq('hirer_id', user.id)
.eq('status', 'confirmed')
.is('paid_at', null)
setAcceptedUnpaid(count || 0)
}

const fetchNewReviews = async () => {
const { data: me } = await supabase
.from('profiles')
.select('last_seen_reviews_at')
.eq('id', user.id)
.maybeSingle()
let q = supabase
.from('reviews')
.select('*', { count: 'exact', head: true })
.eq('reviewee_id', user.id)
if (me?.last_seen_reviews_at) q = q.gt('created_at', me.last_seen_reviews_at)
const { count } = await q
setNewReviews(count || 0)
}

const fetchNewReferrals = async () => {
const { data: me } = await supabase
.from('profiles')
.select('last_seen_referrals_at')
.eq('id', user.id)
.maybeSingle()
setNewReferrals(me?.last_seen_referrals_at ? 0 : 1)
}

const subscribeToUnread = () => {
if (unreadSubRef.current) unreadSubRef.current.unsubscribe()
unreadSubRef.current = supabase
.channel(`header-unread-${user.id}`)
.on('postgres_changes', { event: '*', schema: 'public', table: 'messages' }, () => {
fetchUnreadCount()
})
.subscribe()
}

const subscribeToBookings = () => {
if (bookingSubRef.current) bookingSubRef.current.unsubscribe()
bookingSubRef.current = supabase
.channel(`header-bookings-${user.id}`)
.on('postgres_changes', { event: '*', schema: 'public', table: 'bookings' }, () => {
fetchPendingBookings()
fetchAcceptedUnpaid()
})
.subscribe()
}

const subscribeToReviews = () => {
if (reviewSubRef.current) reviewSubRef.current.unsubscribe()
reviewSubRef.current = supabase
.channel(`header-reviews-${user.id}`)
.on('postgres_changes', { event: '*', schema: 'public', table: 'reviews' }, () => {
fetchNewReviews()
})
.subscribe()
}

const initials = profile?.full_name
? profile.full_name.split(' ').map(n => n[0]).join('').slice(0,2).toUpperCase()
: user?.email?.[0]?.toUpperCase() || '?'

const handleSignOut = async () => {
await signOut()
setUnreadCount(0)
setPendingBookings(0)
navigate('/')
}

return (
<>
<header className="header">
<div className="header-inner">
<Link to="/" className="logo" aria-label="HireIt home">
<span>
<span style={{ color: 'var(--navy)' }}>Hire</span><span style={{ color: 'var(--accent)' }}>It</span>
</span>
</Link>

<nav className="header-nav">
{user ? (
<>
<button className="btn btn-gradient btn-sm" onClick={() => navigate('/list-item')}>
+ List an item
</button>
<div style={{ position: 'relative' }} ref={menuRef}>
<button
onClick={() => setMenuOpen(!menuOpen)}
style={{
background: 'var(--primary)',
border: 'none',
borderRadius: '50%',
width: 36,
height: 36,
display: 'flex',
alignItems: 'center',
justifyContent: 'center',
color: '#fff',
fontWeight: 700,
fontSize: 14,
cursor: 'pointer',
position: 'relative',
boxShadow: '0 2px 6px rgba(45, 63, 204, 0.25)'
}}
>
{profile?.avatar_url ? (
<img src={profile.avatar_url} alt="Profile" style={{ width: 36, height: 36, borderRadius: '50%', objectFit: 'cover' }} />
) : initials}
{unreadCount > 0 && (
<span style={{ position: 'absolute', top: -4, right: -4, background: '#A32D2D', color: 'white', borderRadius: '50%', width: 18, height: 18, fontSize: 10, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', border: '2px solid #fff' }}>
{unreadCount > 9 ? '9+' : unreadCount}
</span>
)}
{(pendingBookings + acceptedUnpaid) > 0 && (
<span style={{ position: 'absolute', bottom: -4, right: -4, background: 'var(--accent)', color: 'white', borderRadius: '50%', width: 18, height: 18, fontSize: 10, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', border: '2px solid #fff' }}>
{(pendingBookings + acceptedUnpaid) > 9 ? '9+' : (pendingBookings + acceptedUnpaid)}
</span>
)}
</button>
{menuOpen && (
<div style={{ position: 'absolute', right: 0, top: '44px', background: '#fff', borderRadius: 12, border: '1px solid var(--border)', minWidth: 200, boxShadow: 'var(--shadow-lg)', zIndex: 200, overflow: 'hidden' }}>
<div style={{ padding: '14px 16px', borderBottom: '1px solid var(--border)', background: 'var(--primary-pale)' }}>
<div style={{ fontWeight: 600, fontSize: 14, color: 'var(--navy)' }}>{profile?.full_name || 'My account'}</div>
<div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>{user.email}</div>
</div>
{[
{ label: '🏠 My listings', path: '/my-listings' },
{ label: '📋 My bookings', path: '/my-bookings', badge: pendingBookings + acceptedUnpaid, badgeColor: 'var(--accent)' },
{ label: '💬 Messages', path: '/messages', badge: unreadCount, badgeColor: '#A32D2D' },
{ label: '👤 Profile', path: '/profile', badge: newReviews, badgeColor: 'var(--primary)' },
{ label: '💳 Payouts', path: '/payouts' },
{ label: '❤️ Favourites', path: '/favourites' },
{ label: '🎁 Referrals', path: '/referrals', badge: newReferrals, badgeColor: 'var(--primary)' },
].map(item => (
<button key={item.path} onClick={() => { navigate(item.path); setMenuOpen(false) }}
style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', textAlign: 'left', padding: '10px 16px', border: 'none', background: 'none', fontSize: 14, cursor: 'pointer', color: 'var(--text)' }}
onMouseEnter={e => e.currentTarget.style.background = 'var(--primary-pale)'}
onMouseLeave={e => e.currentTarget.style.background = 'none'}
>
<span>{item.label}</span>
{item.badge > 0 && (
<span style={{ background: item.badgeColor, color: 'white', borderRadius: '99px', fontSize: 10, fontWeight: 700, padding: '2px 6px', minWidth: 18, textAlign: 'center' }}>
{item.badge > 9 ? '9+' : item.badge}
</span>
)}
</button>
))}
<div style={{ borderTop: '1px solid var(--border)' }}>
<button onClick={handleSignOut}
style={{ display: 'block', width: '100%', textAlign: 'left', padding: '10px 16px', border: 'none', background: 'none', fontSize: 14, fontWeight: 500, cursor: 'pointer', color: 'var(--red)' }}
onMouseEnter={e => e.currentTarget.style.background = 'var(--red-light)'}
onMouseLeave={e => e.currentTarget.style.background = 'none'}
>
Sign out
</button>
</div>
</div>
)}
</div>
</>
) : (
<>
<button className="btn btn-ghost btn-sm" onClick={() => navigate('/auth')}>Sign in</button>
<button className="btn btn-gradient btn-sm" onClick={() => navigate('/auth?mode=signup&from=header')}>Sign up</button>
</>
)}
</nav>
</div>

{showFoundingModal && (
<div className="modal-overlay" onClick={() => setShowFoundingModal(false)}>
<div className="modal-box" onClick={e => e.stopPropagation()}>
<div style={{ textAlign: 'center', marginBottom: 20 }}>
<div style={{
display: 'inline-flex',
alignItems: 'center',
justifyContent: 'center',
width: 72,
height: 72,
borderRadius: '50%',
background: 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)',
color: '#5C3D00',
boxShadow: '0 4px 12px rgba(255, 165, 0, 0.35)',
marginBottom: 14,
}}>
<Trophy size={36} />
</div>
<h3 style={{ fontWeight: 700, fontSize: 20, color: 'var(--navy)', marginBottom: 8 }}>
Be the Founding Lister of {foundingSuburb}!
</h3>
<p style={{ fontSize: 14, color: 'var(--text-muted)', marginBottom: 0, lineHeight: 1.5 }}>
No one's listed in {foundingSuburb} yet — be the first and earn your Founding Lister badge.
</p>
</div>
<div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
<button className="btn btn-ghost" onClick={() => setShowFoundingModal(false)}>
Maybe later
</button>
<button
className="btn btn-gradient"
onClick={() => { setShowFoundingModal(false); navigate('/list-item') }}
>
List an item
</button>
</div>
</div>
</div>
)}
</header>
</>
)
}

